'use client';

import Image from 'next/image';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function DudakDolgusuSonrasiAlkolAlimininSonuclari() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/dudak-dolgusu-sonrasi-alkol-aliminin-sonuclari';
  const pageTitle = 'Dudak Dolgusu Sonrası Alkol Alımının Sonuçları';
  const pageDescription = 'Dudak dolgu işleminden sonra alkol tüketimi hangi sonuçlara yol açar? Morarma, ödem, asimetrik görünüm ve dolgu yer değişimi üzerine bilimsel açıklamalar ve uzman önerileri.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-pink-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Dudak Dolgusu Sonrası Alkol Alımının Sonuçları', href: '/tr/blog/dudak-dolgusu-sonrasi-alkol-aliminin-sonuclari' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Dudak dolgusu sonrası alkol alımının sonuçları"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-rose-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Dudak Bakımı
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Dudak Dolgusu Sonrası Alkol Alımının Sonuçları
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>2 Mart 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>6 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Dudak dolgu uygulaması, yüzün merkezinde doğallığı ve ifade dengesini sağlayan en hassas estetik işlemlerden biridir.
                Ancak bu hassas denge, yalnızca uygulama kalitesiyle değil; sonrası bakım ve yaşam alışkanlıklarıyla da yakından ilgilidir.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Uygulama sonrası en önemli konulardan biri alkol tüketimidir.
                Basit bir "bir kadeh şarap" bile dolgunun dokuda yerleşimini etkileyebilir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣ Alkol ve Dudak Dokusunun Biyolojisi
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Dudak bölgesi, vücuttaki en yoğun damar ağına sahip alanlardan biridir.
                Cilt altı tabaka ince, kılcal damarlarla örülüdür ve her enjeksiyonda mikrotravma oluşur.
              </p>

              <div className="bg-rose-50 rounded-xl p-6 mb-6">
                <p className="text-rose-800 mb-4 font-medium">
                  Alkol alındığında:
                </p>
                <ul className="list-disc list-inside space-y-2 text-rose-800">
                  <li>Damarlar genişler (vazodilatasyon),</li>
                  <li>Kan akışı artış gösterir,</li>
                  <li>Damar duvarı geçirgenliği yükselir,</li>
                  <li>Hücre dışı sıvı artışı (ödem) oluşur.</li>
                </ul>
              </div>

              <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-6">
                <p className="text-orange-800 font-medium">
                  📍 Bu durum, henüz yerleşmemiş olan dolgunun dokuda dağılmasına neden olabilir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2️⃣ Dudak Dolgusu Sonrası Alkolün Klinik Sonuçları
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-rose-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Etkilenme Alanı</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Sonuç</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Açıklama</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Mikro dolaşım</td>
                      <td className="border border-gray-300 px-4 py-3">Morarma (ekimoz)</td>
                      <td className="border border-gray-300 px-4 py-3">Damar genişlemesiyle kan sızıntısı artar</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Lenf sistemi</td>
                      <td className="border border-gray-300 px-4 py-3">Ödem</td>
                      <td className="border border-gray-300 px-4 py-3">Alkol lenfatik akımdan daha fazla sıvı çekilmesine neden olur</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Hyaluronik asit</td>
                      <td className="border border-gray-300 px-4 py-3">Hacim dalgalanması</td>
                      <td className="border border-gray-300 px-4 py-3">Su tutulumundaki ani değişimler dolgunun hacmini bozar</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Simetri ve form</td>
                      <td className="border border-gray-300 px-4 py-3">Geçici asimetrik görünüm</td>
                      <td className="border border-gray-300 px-4 py-3">Alt ve üst dudak dengesi bozulabilir</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Klinik iyileşme</td>
                      <td className="border border-gray-300 px-4 py-3">Gecikme</td>
                      <td className="border border-gray-300 px-4 py-3">Asetaldehit dokularda rejenerasyonu yavaşlatır</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3️⃣ Görsel Sonuçlar ve Estetik Algı
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Estetik psikolojide yüzün en çok dikkat çeken noktası "ağız bölgesi"dir.
                Alkol sonrası ödem ve şişlik, dudakların oranlarını bozar ve hastada "dolgum kaydı" ya da "yamuk oldu" endişesi yaratır.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Oysa bu çoğunlukla geçici bir fizyolojik reaksiyondur.
                Gerçek bozulma değil, damar ve lenf sisteminin reaktif cevabıdır.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4️⃣ Zaman Çizelgesi: Alkolün Etkisi Ne Kadar Sürer?
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-purple-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Süre</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Olası Etkiler</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Açıklama</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">İlk 6 saat</td>
                      <td className="border border-gray-300 px-4 py-3">Vazodilatasyon</td>
                      <td className="border border-gray-300 px-4 py-3">Damar genişlemesi başlar</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">6–24 saat</td>
                      <td className="border border-gray-300 px-4 py-3">Ödem ve morarma</td>
                      <td className="border border-gray-300 px-4 py-3">Mikrotravmalar belirginleşir</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">24–48 saat</td>
                      <td className="border border-gray-300 px-4 py-3">Dolgu yerleşme dönemi</td>
                      <td className="border border-gray-300 px-4 py-3">En riskli zaman dilimi</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">48 saat sonrası</td>
                      <td className="border border-gray-300 px-4 py-3">Stabilizasyon</td>
                      <td className="border border-gray-300 px-4 py-3">Dolgu dokuya entegrasyon sağlar</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
                <p className="text-blue-800 font-medium">
                  💬 Bu nedenle, işlemden sonra en az 48 saat boyunca alkol almamak zorunlu bir kuraldır.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5️⃣ Klinik Gözlemden Bir Vaka
              </h2>
              
              <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-6">
                <p className="text-blue-800 italic">
                  "Bir hastam işlemden bir gün sonra düğün kutlamasında şampanya içmişti.
                  Ertesi gün üst dudakta asimetrik şişlik gözlemledik.
                  Dolgu kayması değildi, ödem nedeniyle gelişmişti.
                  Soğuk kompres ve drenaj ile 3 günde tamamen düzeldi."
                </p>
                <p className="text-blue-800 font-medium mt-4">
                  — Dr. Barış Çin, Estetik Cerrahi Uzmanı
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6️⃣ Morarma ve Ödem Yönetimi
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Alkol tüketildiyse ve morarma geliştiyse:
              </p>

              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-green-800">
                  <li>Soğuk kompres (15 dk aralıklarla)</li>
                  <li>Bol su tüketimi</li>
                  <li>Yüksek yastıkta uyuma</li>
                  <li>Tuzlu gıdalardan kaçınma</li>
                </ul>
                <p className="text-green-800 mt-4">
                  Bu önlemler reaktif ödemi kısa sürede indirir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                7️⃣ Psikolojik Boyut
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Estetik uygulamalardan sonra hastalar genellikle "erken sonuç beklentisi" içindedir.
                Alkolün yarattığı şişlik ve asimetriler, hastada hayal kırıklığı ve stres yaratabilir.
                Bu noktada hekim ile iletişim önemlidir.
              </p>

              <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 mb-6">
                <p className="text-indigo-800 font-medium">
                  "Görsel bozukluk geçici olabilir, sabır kalıcı güzelliğin anahtarıdır."
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                8️⃣ Alkolün Dolgu Kalıcılığına Etkisi
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Hyaluronik asit maddesi vücutta doğal olarak metabolize edilir.
                Alkolün karaciğer üzerindeki yükü, bu metabolizma sürecini hızlandırabilir.
                Yani alkol sık tüketen kişilerde dolgular daha hızlı erir.
              </p>

              <div className="bg-yellow-50 rounded-xl p-6 mb-6">
                <div className="text-center">
                  <p className="text-yellow-800 font-medium mb-2">Kalıcılık Süresi Karşılaştırması</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-yellow-100 rounded-lg p-4">
                      <p className="text-yellow-900 font-semibold">Normal Kullanım</p>
                      <p className="text-yellow-800 text-2xl font-bold">12 ay</p>
                    </div>
                    <div className="bg-red-100 rounded-lg p-4">
                      <p className="text-red-900 font-semibold">Yoğun Alkol Kullanımı</p>
                      <p className="text-red-800 text-2xl font-bold">8-9 ay</p>
                    </div>
                  </div>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                9️⃣ Etik ve Klinik Öneri
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Estetik uygulamalar "anlık güzellik" değil, "bütüncül sağlık" yaklaşımıdır.
                Bir şişe şarap zevki, günlerce devam edecek şişlik ve pişmanlığa dönüşebilir.
              </p>

              <div className="bg-pink-50 border-l-4 border-pink-400 p-4 mb-6">
                <p className="text-pink-800 font-medium">
                  💬 Güzelliğin de disiplini vardır; onu korumak sabır ister.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                🔟 Sık Sorulan Sorular (FAQ)
              </h2>
              
              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">1. Dolgu sonrası alkol aldım, ne olur?</h3>
                  <p className="text-gray-600">Geçici ödem ve morarma oluşabilir, soğuk kompres önerilir.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">2. Dolgu yer değiştirir mi?</h3>
                  <p className="text-gray-600">Erken dönemde alkol dolaşımı artırırsa, mikro kayma riski vardır.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">3. Kalıcılığı etkiler mi?</h3>
                  <p className="text-gray-600">Evet, metabolizmayı hızlandırarak etki süresini kısaltabilir.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">4. Ne zaman alkol serbest?</h3>
                  <p className="text-gray-600">En az 48 saat, tercihen 72 saat sonra.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">5. Şişlik ne kadar sürer?</h3>
                  <p className="text-gray-600">Alkol sonrası ödem genellikle 2-4 gün içinde azalır.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">6. Dolgu bozulur mu?</h3>
                  <p className="text-gray-600">Kalıcı bozulma olmaz, sadece geçici görsel değişiklikler olur.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sonuç ve Klinik Değerlendirme
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Dudak dolgu sonrası alkol tüketimi, küçük bir alışkanlık gibi görünse de estetik sonucun kalitesini belirleyen kritik bir detaydır.
                Hyaluronik asit molekülünün dokuyla bütünleşmesi için stabil bir mikro çevre gereklidir; alkol bu dengeyi bozar.
              </p>

              <div className="bg-red-50 border-l-4 border-red-400 p-6 mb-6">
                <p className="text-red-800 font-medium">
                  🩺 Dr. Barış Çin öneriyor:
                </p>
                <p className="text-red-800 mt-2">
                  "Dolgu işleminden sonra ilk 48 saat vücudun yeni denge kurduğu dönemdir.
                  Bu süreyi alkolsüz, sakin ve bol su ile geçirin; sonuç farkı fotoğraflarda bile görülür."
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              
              <div className="bg-gray-50 rounded-xl p-6 mb-8">
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>ISAPS Global Report (2024) – "Dermal Filler Complications and Alcohol Interaction"</li>
                  <li>Harvard Health (2023) – "Ethanol Metabolism and Microcirculation"</li>
                  <li>Teoxane Clinical Studies (2024) – "Hyaluronic Acid Behavior in Reactive Edema"</li>
                  <li>Türk Estetik Tıp Derneği (TETD) – "Dolgu Sonrası Bakım Kılavuzu"</li>
                </ul>
              </div>

              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-rose-100 text-rose-800 px-3 py-1 rounded-full text-sm">#dudakdolgu</span>
                <span className="bg-rose-100 text-rose-800 px-3 py-1 rounded-full text-sm">#alkol</span>
                <span className="bg-rose-100 text-rose-800 px-3 py-1 rounded-full text-sm">#ödem</span>
                <span className="bg-rose-100 text-rose-800 px-3 py-1 rounded-full text-sm">#morarma</span>
                <span className="bg-rose-100 text-rose-800 px-3 py-1 rounded-full text-sm">#estetik</span>
                <span className="bg-rose-100 text-rose-800 px-3 py-1 rounded-full text-sm">#DrBarışÇin</span>
              </div>

              {/* Social Share Buttons */}
              <div className="border-t pt-8 mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Bu yazıyı paylaş:</h3>
                <div className="flex space-x-4">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FaFacebookF className="w-4 h-4" />
                    <span>Facebook</span>
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="flex items-center space-x-2 bg-blue-400 text-white px-4 py-2 rounded-lg hover:bg-blue-500 transition-colors"
                  >
                    <FaTwitter className="w-4 h-4" />
                    <span>Twitter</span>
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="flex items-center space-x-2 bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors"
                  >
                    <FaLinkedinIn className="w-4 h-4" />
                    <span>LinkedIn</span>
                  </button>
                  <button
                    onClick={() => handleShare('whatsapp')}
                    className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
                  >
                    <FaWhatsapp className="w-4 h-4" />
                    <span>WhatsApp</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* CTA Section */}
        <div className="mt-12 bg-gradient-to-r from-rose-600 to-pink-700 rounded-3xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">Dudak Dolgusu Uygulaması ve Bakım Danışmanlığı</h3>
          <p className="mb-6">Uzman doktorumuzdan dudak dolgusu uygulaması ve sonrası bakım hakkında detaylı bilgi alın.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`https://wa.me/905438999586?text=${encodeURIComponent('Dudak dolgusu uygulaması ve sonrası bakım hakkında bilgi almak istiyorum.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
            >
              <FaWhatsapp className="w-5 h-5 mr-2" />
              WhatsApp ile İletişim
            </a>
            <a
              href="/tr/iletisim"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-rose-600 rounded-full hover:bg-gray-100 transition-colors"
            >
              Randevu Al
            </a>
          </div>
        </div>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <a
              href="/tr/blog/dudak-dolgusu-sonrasi-alkol-tuketimi-bilimsel-etkiler-klinik-bulgular-ve-estetik-sonuclar"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Dudak Dolgusu Sonrası Alkol Tüketimi</h4>
              <p className="text-gray-600 text-sm">Dudak dolgusu sonrası alkol neden önerilmez? Hyaluronik asit dengesi ve damar geçirgenliği.</p>
            </a>
            <a
              href="/tr/blog/dudak-dolgusu-teknikleri-dogal-simetrik-ve-estetik-sonuclar"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Dudak Dolgusu Teknikleri</h4>
              <p className="text-gray-600 text-sm">Russian Lips, Paris Lips, Volume Lips gibi tekniklerin farkları ve uygulama süreci.</p>
            </a>
            <a
              href="/tr/blog/en-iyi-dudak-dolgusu-markasi-dogallik-kalicilik-ve-guven"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">En İyi Dudak Dolgusu Markası</h4>
              <p className="text-gray-600 text-sm">Kalıcılığı, doğallığı, FDA onayı ve güvenilirliğiyle öne çıkan dudak dolgularının karşılaştırması.</p>
            </a>
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              <p className="text-gray-600 mb-4">
                Daha fazla bilgilendirici içerik için bizi takip edin.
              </p>
              <a
                href="/tr/blog"
                className="inline-flex items-center px-4 py-2 bg-rose-600 text-white rounded-full hover:bg-rose-700 transition-colors text-sm"
              >
                Tüm Blog Yazılarını Görüntüle
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
